import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Book, Star, ChevronDown, ChevronUp, Sun, Moon, Compass, Orbit, LayoutGrid, Sparkles, Calendar, Brain, Flame, Droplet, Wind, Mountain, Crown, Lock, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Les 12 signes
const ZODIAC_SIGNS = [
  { name: "Bélier", symbol: "♈", element: "Feu", quality: "Cardinal", ruler: "Mars", dates: "21 mars - 19 avril", traits: "Pionnier, courageux, impulsif", color: "from-red-500 to-orange-500" },
  { name: "Taureau", symbol: "♉", element: "Terre", quality: "Fixe", ruler: "Vénus", dates: "20 avril - 20 mai", traits: "Stable, sensuel, déterminé", color: "from-green-600 to-emerald-500" },
  { name: "Gémeaux", symbol: "♊", element: "Air", quality: "Mutable", ruler: "Mercure", dates: "21 mai - 20 juin", traits: "Communicatif, curieux, adaptable", color: "from-yellow-400 to-amber-500" },
  { name: "Cancer", symbol: "♋", element: "Eau", quality: "Cardinal", ruler: "Lune", dates: "21 juin - 22 juillet", traits: "Protecteur, émotionnel, intuitif", color: "from-slate-400 to-slate-500" },
  { name: "Lion", symbol: "♌", element: "Feu", quality: "Fixe", ruler: "Soleil", dates: "23 juillet - 22 août", traits: "Charismatique, généreux, fier", color: "from-amber-500 to-yellow-500" },
  { name: "Vierge", symbol: "♍", element: "Terre", quality: "Mutable", ruler: "Mercure", dates: "23 août - 22 sept", traits: "Analytique, serviable, perfectionniste", color: "from-emerald-600 to-teal-500" },
  { name: "Balance", symbol: "♎", element: "Air", quality: "Cardinal", ruler: "Vénus", dates: "23 sept - 22 oct", traits: "Harmonieux, diplomate, esthète", color: "from-pink-400 to-rose-500" },
  { name: "Scorpion", symbol: "♏", element: "Eau", quality: "Fixe", ruler: "Pluton/Mars", dates: "23 oct - 21 nov", traits: "Intense, transformateur, passionné", color: "from-red-700 to-rose-700" },
  { name: "Sagittaire", symbol: "♐", element: "Feu", quality: "Mutable", ruler: "Jupiter", dates: "22 nov - 21 déc", traits: "Optimiste, aventurier, philosophe", color: "from-purple-500 to-violet-500" },
  { name: "Capricorne", symbol: "♑", element: "Terre", quality: "Cardinal", ruler: "Saturne", dates: "22 déc - 19 jan", traits: "Ambitieux, discipliné, responsable", color: "from-slate-600 to-slate-700" },
  { name: "Verseau", symbol: "♒", element: "Air", quality: "Fixe", ruler: "Uranus/Saturne", dates: "20 jan - 18 fév", traits: "Innovateur, humanitaire, indépendant", color: "from-sky-500 to-cyan-500" },
  { name: "Poissons", symbol: "♓", element: "Eau", quality: "Mutable", ruler: "Neptune/Jupiter", dates: "19 fév - 20 mars", traits: "Intuitif, créatif, empathique", color: "from-indigo-400 to-purple-500" }
];

// Les planètes
const PLANETS = [
  { name: "Soleil", symbol: "☉", domain: "Identité, ego, vitalité", cycle: "1 an", description: "Le Soleil représente votre essence, votre conscience de soi et ce qui vous fait briller." },
  { name: "Lune", symbol: "☽", domain: "Émotions, inconscient, besoins", cycle: "28 jours", description: "La Lune gouverne vos émotions profondes, vos réactions instinctives et vos besoins de sécurité." },
  { name: "Mercure", symbol: "☿", domain: "Communication, pensée, intellect", cycle: "88 jours", description: "Mercure influence votre façon de penser, communiquer et traiter l'information." },
  { name: "Vénus", symbol: "♀", domain: "Amour, beauté, valeurs", cycle: "225 jours", description: "Vénus représente vos valeurs, ce que vous aimez et comment vous aimez." },
  { name: "Mars", symbol: "♂", domain: "Action, énergie, désir", cycle: "2 ans", description: "Mars est votre énergie d'action, votre volonté et votre façon de poursuivre vos désirs." },
  { name: "Jupiter", symbol: "♃", domain: "Expansion, chance, sagesse", cycle: "12 ans", description: "Jupiter apporte croissance, optimisme et opportunités d'expansion." },
  { name: "Saturne", symbol: "♄", domain: "Structure, discipline, leçons", cycle: "29 ans", description: "Saturne enseigne la discipline, la patience et apporte les leçons de maturité." },
  { name: "Uranus", symbol: "♅", domain: "Révolution, originalité, éveil", cycle: "84 ans", description: "Uranus provoque les changements soudains et l'innovation." },
  { name: "Neptune", symbol: "♆", domain: "Rêves, intuition, spiritualité", cycle: "165 ans", description: "Neptune dissout les frontières et connecte au spirituel et à l'imaginaire." },
  { name: "Pluton", symbol: "♇", domain: "Transformation, pouvoir, renaissance", cycle: "248 ans", description: "Pluton apporte les transformations profondes et les renaissances." }
];

// Les 12 maisons
const HOUSES = [
  { number: 1, name: "Ascendant", domain: "Identité, apparence, premiers pas", question: "Qui suis-je ?" },
  { number: 2, name: "Maison II", domain: "Ressources, finances, valeurs", question: "Qu'est-ce qui a de la valeur pour moi ?" },
  { number: 3, name: "Maison III", domain: "Communication, apprentissage, voisinage", question: "Comment je communique ?" },
  { number: 4, name: "Fond du Ciel", domain: "Foyer, racines, famille", question: "D'où je viens ?" },
  { number: 5, name: "Maison V", domain: "Créativité, plaisir, enfants", question: "Qu'est-ce qui me rend joyeux ?" },
  { number: 6, name: "Maison VI", domain: "Travail quotidien, santé, service", question: "Comment je sers ?" },
  { number: 7, name: "Descendant", domain: "Partenariats, mariage, contrats", question: "Qu'est-ce que je cherche chez l'autre ?" },
  { number: 8, name: "Maison VIII", domain: "Transformation, intimité, ressources partagées", question: "Qu'est-ce que je dois transformer ?" },
  { number: 9, name: "Maison IX", domain: "Voyages, philosophie, enseignement supérieur", question: "Quel sens je donne à la vie ?" },
  { number: 10, name: "Milieu du Ciel", domain: "Carrière, réputation, vocation", question: "Quelle est ma place dans le monde ?" },
  { number: 11, name: "Maison XI", domain: "Amis, groupes, projets collectifs", question: "Quels sont mes espoirs ?" },
  { number: 12, name: "Maison XII", domain: "Inconscient, spiritualité, retraite", question: "Qu'est-ce que je dois libérer ?" }
];

// Les aspects
const ASPECTS = [
  { name: "Conjonction", symbol: "☌", angle: "0°", nature: "Fusion", description: "Union intense des énergies - amplification mutuelle" },
  { name: "Sextile", symbol: "⚹", angle: "60°", nature: "Harmonieux", description: "Opportunités et talents - collaboration facile" },
  { name: "Carré", symbol: "□", angle: "90°", nature: "Tension", description: "Défi dynamique - moteur de croissance" },
  { name: "Trigone", symbol: "△", angle: "120°", nature: "Harmonieux", description: "Flux naturel - facilité et talents innés" },
  { name: "Opposition", symbol: "☍", angle: "180°", nature: "Polarité", description: "Conscience et équilibre - intégration des opposés" }
];

const ModuleCard = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/60 rounded-3xl border border-sky-200 shadow-lg overflow-hidden backdrop-blur-sm"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-6 flex items-center justify-between bg-gradient-to-r ${color} transition-colors`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="h-6 w-6 text-gray-900" />
          </div>
          <h2 className="text-xl md:text-2xl text-gray-900 text-left font-bold">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="h-6 w-6 text-gray-900" />
        ) : (
          <ChevronDown className="h-6 w-6 text-gray-900" />
        )}
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-6"
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );
};

const AstrozodiacKnowledgePage = () => {
  const navigate = useNavigate();
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/premium/check/astrozodiac`, {
          withCredentials: true
        });
        setHasPremiumAccess(response.data.has_access === true);
      } catch (error) {
        setHasPremiumAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, []);

  const getElementIcon = (element) => {
    switch (element) {
      case 'Feu': return <Flame className="h-4 w-4 text-orange-400" />;
      case 'Terre': return <Mountain className="h-4 w-4 text-green-400" />;
      case 'Air': return <Wind className="h-4 w-4 text-sky-600" />;
      case 'Eau': return <Droplet className="h-4 w-4 text-blue-400" />;
      default: return null;
    }
  };

  // Loading state
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  // Premium upsell
  if (!hasPremiumAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/astrozodiac')}
            className="mb-6 rounded-full text-gray-600 hover:bg-sky-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full border border-sky-200 mb-4">
              <Lock className="h-4 w-4 text-sky-600" />
              <span className="text-sky-700 text-sm font-medium">Premium</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Découvrir l'Astrologie</h1>
            <p className="text-gray-600">Accédez à la base de connaissances complète</p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-3xl border-2 border-sky-200 p-8 shadow-xl">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg">
                  <Crown className="h-10 w-10 text-white" />
                </div>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "Les 12 signes du zodiaque",
                  "Les 10 planètes et leur influence",
                  "Les 12 maisons astrologiques",
                  "Les aspects et transits",
                  "Calcul de la carte du ciel"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Sparkles className="h-4 w-4 text-sky-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => navigate('/premium')}
                className="w-full bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg"
                data-testid="upgrade-premium-btn"
              >
                <Crown className="h-5 w-5 mr-2" />
                Devenir Premium
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                À partir de 1.99€ pour 7 jours d'essai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 py-12 px-4 md:px-8">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-cyan-300 rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/astrozodiac')}
            className="mb-6 rounded-full text-sky-500 hover:bg-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="bg-gradient-to-br from-cyan-600 via-blue-600 to-purple-600 p-8 md:p-10 rounded-3xl shadow-xl mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Book className="h-10 w-10 text-gray-900" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl mb-3 text-gray-900 font-bold">
                  Découvrir l'Astrologie
                </h1>
                <p className="text-gray-900/90 text-base md:text-lg mb-4">
                  Le langage des étoiles et des cycles cosmiques
                </p>
                <p className="text-gray-900/80 text-sm max-w-2xl">
                  L'astrologie est un système symbolique millénaire qui utilise les positions des astres 
                  pour éclairer les tendances et potentiels. Les astres inclinent, ils n'obligent pas.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { icon: Star, label: "Les Signes", color: "bg-amber-500/20 text-amber-700 border-amber-200" },
              { icon: Orbit, label: "Planètes", color: "bg-purple-500/20 text-purple-300 border-purple-200" },
              { icon: LayoutGrid, label: "Maisons", color: "bg-cyan-500/20 text-sky-500 border-sky-200" },
              { icon: Compass, label: "Aspects", color: "bg-rose-500/20 text-rose-700 border-rose-200" }
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-3 rounded-xl text-center border`}>
                <item.icon className="h-5 w-5 mx-auto mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Modules */}
        <div className="space-y-6">
          
          {/* Module 1: Introduction */}
          <ModuleCard 
            title="Les Fondamentaux" 
            icon={Sparkles}
            color="from-cyan-600 to-blue-600"
            defaultOpen={true}
          >
            <div className="space-y-6">
              <div className="bg-white p-5 rounded-xl border border-sky-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Qu'est-ce que l'astrologie ?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  L'astrologie est l'étude de la corrélation entre les positions des astres et les événements terrestres. 
                  Un thème natal (ou carte du ciel) est une photographie du ciel au moment exact de votre naissance, 
                  révélant vos tendances, potentiels et défis de vie.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl border border-amber-500/20">
                  <Sun className="h-8 w-8 text-amber-400 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Signe Solaire</h4>
                  <p className="text-xs text-gray-700">Votre identité fondamentale, votre ego et votre volonté. C'est "votre signe" habituel.</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-400/20">
                  <Moon className="h-8 w-8 text-gray-700 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Signe Lunaire</h4>
                  <p className="text-xs text-gray-700">Vos émotions, besoins profonds et réactions instinctives. Votre monde intérieur.</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-sky-100">
                  <Star className="h-8 w-8 text-sky-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Ascendant</h4>
                  <p className="text-xs text-gray-700">Votre masque social, première impression et la façon dont vous abordez la vie.</p>
                </div>
              </div>
            </div>
          </ModuleCard>

          {/* Module 2: Les 12 Signes */}
          <ModuleCard 
            title="Les 12 Signes du Zodiaque" 
            icon={Star}
            color="from-amber-500 to-orange-500"
          >
            <p className="text-gray-700 mb-6 text-sm">
              Les 12 signes représentent 12 archétypes d'énergie. Chacun est associé à un élément (Feu, Terre, Air, Eau) 
              et une qualité (Cardinal, Fixe, Mutable).
            </p>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {ZODIAC_SIGNS.map((sign) => (
                <div key={sign.name} className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`w-10 h-10 bg-gradient-to-br ${sign.color} rounded-full flex items-center justify-center text-gray-900 font-bold text-lg`}>
                      {sign.symbol}
                    </span>
                    <div>
                      <h4 className="font-bold text-gray-900">{sign.name}</h4>
                      <p className="text-xs text-gray-600">{sign.dates}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    {getElementIcon(sign.element)}
                    <span className="text-gray-600">{sign.element} • {sign.quality}</span>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">Maître : {sign.ruler}</p>
                  <p className="text-xs text-sky-600">{sign.traits}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 3: Les Planètes */}
          <ModuleCard 
            title="Les Planètes" 
            icon={Orbit}
            color="from-purple-500 to-indigo-500"
          >
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 mb-6">
              <p className="text-purple-200 text-sm">
                Les planètes représentent différentes fonctions psychologiques et types d'énergie. 
                Leur position en signe et en maison nuance leur expression.
              </p>
            </div>
            <div className="space-y-3">
              {PLANETS.map((planet) => (
                <div key={planet.name} className="bg-white p-4 rounded-xl border border-purple-500/20 flex items-start gap-4">
                  <span className="w-12 h-12 bg-purple-600/30 rounded-full flex items-center justify-center text-purple-300 font-bold text-xl flex-shrink-0">
                    {planet.symbol}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-bold text-gray-900">{planet.name}</h4>
                      <span className="text-xs text-gray-600">Cycle: {planet.cycle}</span>
                    </div>
                    <p className="text-xs text-purple-300 mb-1">{planet.domain}</p>
                    <p className="text-xs text-gray-600">{planet.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 4: Les 12 Maisons */}
          <ModuleCard 
            title="Les 12 Maisons" 
            icon={LayoutGrid}
            color="from-cyan-500 to-teal-500"
          >
            <div className="bg-sky-50 p-4 rounded-xl border border-sky-200 mb-6">
              <p className="text-cyan-200 text-sm">
                Les maisons divisent le thème en 12 secteurs de vie. Chaque maison représente un domaine 
                où s'expriment les énergies des planètes qui s'y trouvent.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {HOUSES.map((house) => (
                <div key={house.number} className="bg-white p-4 rounded-xl border border-sky-100">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-gray-900 font-bold">
                      {house.number}
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm">{house.name}</h4>
                  </div>
                  <p className="text-xs text-sky-500 mb-2">{house.domain}</p>
                  <p className="text-xs text-gray-600 italic">"{house.question}"</p>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 5: Les Aspects */}
          <ModuleCard 
            title="Les Aspects Planétaires" 
            icon={Compass}
            color="from-rose-500 to-pink-500"
          >
            <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 mb-6">
              <p className="text-rose-200 text-sm">
                Les aspects sont les angles formés entre les planètes. Ils créent des dynamiques 
                harmonieuses ou tendues qui façonnent votre personnalité et vos expériences.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ASPECTS.map((aspect) => (
                <div key={aspect.name} className={`bg-white p-4 rounded-xl border ${
                  aspect.nature === 'Harmonieux' ? 'border-sky-100' : 
                  aspect.nature === 'Tension' ? 'border-amber-500/20' : 'border-rose-500/20'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl text-gray-900">{aspect.symbol}</span>
                    <div>
                      <h4 className="font-bold text-gray-900">{aspect.name}</h4>
                      <p className="text-xs text-gray-600">{aspect.angle}</p>
                    </div>
                  </div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs mb-2 ${
                    aspect.nature === 'Harmonieux' ? 'bg-sky-100 text-sky-500' : 
                    aspect.nature === 'Tension' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                  }`}>{aspect.nature}</span>
                  <p className="text-xs text-gray-700">{aspect.description}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 6: Les Transits */}
          <ModuleCard 
            title="Les Transits & Cycles" 
            icon={Calendar}
            color="from-amber-400 to-yellow-500"
          >
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 mb-6">
              <h4 className="font-semibold text-amber-200 mb-2">Qu'est-ce qu'un transit ?</h4>
              <p className="text-amber-700 text-sm">
                Les transits sont les positions actuelles des planètes par rapport à votre thème natal. 
                Ils indiquent les thèmes et énergies du moment présent.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Transits rapides", planets: "Soleil, Lune, Mercure, Vénus, Mars", duration: "Jours à semaines", description: "Influences quotidiennes et ambiances passagères" },
                { title: "Transits lents", planets: "Jupiter, Saturne", duration: "Mois à années", description: "Thèmes majeurs de croissance et de restructuration" },
                { title: "Transits générationnels", planets: "Uranus, Neptune, Pluton", duration: "Années à décennies", description: "Transformations profondes et évolutions collectives" },
                { title: "Retour de Saturne", planets: "Saturne", duration: "~29 ans", description: "Passages majeurs vers la maturité (29-30 ans, 58-60 ans)" }
              ].map((item, i) => (
                <div key={i} className="bg-white p-4 rounded-xl border border-amber-500/20">
                  <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-xs text-amber-700 mb-2">{item.planets}</p>
                  <p className="text-xs text-gray-600 mb-1">Durée: {item.duration}</p>
                  <p className="text-xs text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-700 mb-6">
            Prêt à explorer ton propre thème astral ?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              data-testid="start-bigthree-btn"
              onClick={() => navigate('/astrozodiac/big-three')}
              size="lg"
              className="rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400 text-gray-900 px-8 py-6 text-base font-semibold shadow-lg"
            >
              <Sun className="mr-2 h-5 w-5" />
              Découvrir mes Big Three
            </Button>
            <Button
              onClick={() => navigate('/astrozodiac/quiz')}
              size="lg"
              variant="outline"
              className="rounded-full border-sky-300 text-sky-500 hover:bg-sky-50 px-8 py-6 text-base font-semibold"
            >
              <Brain className="mr-2 h-5 w-5" />
              Tester mes connaissances
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AstrozodiacKnowledgePage;
